import { useState, useEffect } from 'react';
import { useReferralStore } from './referralStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button, Input, Alert, AlertDescription } from '@nimbitlabs/ui';
import { Copy, Check, Users, Gift, TrendingUp, Loader2 } from 'lucide-react';

export function ReferralsPage() {
  const { info: referralInfo, stats: referralStats, loading, error, fetchReferralInfo, fetchReferralStats } = useReferralStore();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralInfo();
    fetchReferralStats();
  }, [fetchReferralInfo, fetchReferralStats]);

  const isLoading = loading;
  const infoError = error;
  const statsError = error;

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load referral information</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">
          Referral Program
        </h1>
        <p className="text-muted-foreground">
          Invite others and earn rewards together
        </p>
      </div>

      {/* Referral Stats Overview */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card className="relative overflow-hidden">
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Referrals</CardTitle>
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold mb-1">{referralStats?.totalReferrals || 0}</div>
            <p className="text-sm text-muted-foreground">
              Companies you've referred
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Referrals</CardTitle>
            <div className="p-2 rounded-lg bg-primary/10">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold mb-1">{referralStats?.activeReferrals || 0}</div>
            <p className="text-sm text-muted-foreground">
              Currently active accounts
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">Your Referral Code</CardTitle>
            <div className="p-2 rounded-lg bg-primary/10">
              <Gift className="h-5 w-5 text-primary" />
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="text-4xl font-bold font-mono mb-1 text-primary">
              {referralInfo?.referralCode || '—'}
            </div>
            <p className="text-sm text-muted-foreground">
              Share this code with others
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Referral Link Card */}
      <Card className="relative overflow-hidden mt-6">
        <CardHeader className="relative pb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Copy className="h-5 w-5 text-primary" />
            </div>
            <CardTitle>Share Your Referral Link</CardTitle>
          </div>
          <CardDescription>
            Share this link with others to invite them. Both you and your referrals will receive special benefits!
          </CardDescription>
        </CardHeader>
        <CardContent className="relative space-y-4">
          <div className="flex gap-3">
            <Input
              value={referralInfo?.referralUrl || ''}
              readOnly
              className="font-mono text-sm"
            />
            <Button
              onClick={() => copyToClipboard(referralInfo?.referralUrl || '')}
              className="shrink-0 min-w-[120px]"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Link
                </>
              )}
            </Button>
          </div>

          {referralInfo?.referredByCode && (
            <Alert>
              <Gift className="h-4 w-4 text-primary" />
              <AlertDescription className="text-sm">
                You were referred using code: <span className="font-mono font-semibold text-primary">{referralInfo.referredByCode}</span>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Referred Accounts List */}
      {referralStats && referralStats.referredAccounts.length > 0 && (
        <Card className="relative overflow-hidden mt-6">
          <CardHeader className="relative">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <CardTitle>Your Referrals</CardTitle>
            </div>
            <CardDescription>
              Companies that signed up using your referral code
            </CardDescription>
          </CardHeader>
          <CardContent className="relative">
            <div className="space-y-3">
              {referralStats.referredAccounts.map((account) => (
                <div
                  key={account.accountId}
                  className="flex items-center justify-between p-5 border rounded-xl transition-colors"
                >
                  <div className="flex-1">
                    <h4 className="font-semibold mb-1">{account.companyName}</h4>
                    <p className="text-sm text-muted-foreground mb-1">{account.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Joined {new Date(account.createdAt).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                  <div>
                    {account.isActive ? (
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Benefits Information */}
      <Card className="relative overflow-hidden mt-6">
        <CardHeader className="relative">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-primary/10">
              <Gift className="h-5 w-5 text-primary" />
            </div>
            <CardTitle>Referral Benefits</CardTitle>
          </div>
          <CardDescription>
            What you and your referrals receive
          </CardDescription>
        </CardHeader>
        <CardContent className="relative">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="flex gap-4 p-6 border rounded-xl">
              <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Gift className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold mb-2">For You</h4>
                <p className="text-sm text-muted-foreground">
                  Earn rewards for each person you refer
                </p>
              </div>
            </div>
            <div className="flex gap-4 p-6 border rounded-xl">
              <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold mb-2">For Your Referrals</h4>
                <p className="text-sm text-muted-foreground">
                  They get special discounts and benefits when signing up
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
